import React, { useState } from "react";
import axios from "axios";
import { MailCheck } from "lucide-react";
import { getApiUrl as resolveApiUrl } from "@/lib/runtimeConfig";
import { formatApiErrorDetail } from "@/lib/api";
import { AUTH_BUTTON_CLS, AUTH_INPUT_CLS, AuthError, AuthShell, BackToLogin } from "@/components/auth/AuthShell";

const API = resolveApiUrl();

export default function ForgotPasswordPage() {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        setBusy(true);
        try {
            await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
            setSent(true);
        } catch (e2) {
            setError(formatApiErrorDetail(e2?.response?.data?.detail) || "Could not send the reset link.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <AuthShell kicker="Password reset" title="Forgot your password?" testId="forgot-password-page">
            {sent ? (
                <div data-testid="forgot-password-sent">
                    <div className="bg-[#7C3AED]/10 border border-[#7C3AED]/30 rounded-md p-4 flex gap-3">
                        <MailCheck className="w-5 h-5 text-[#a17bff] shrink-0 mt-0.5" strokeWidth={1.6} />
                        <div className="text-sm text-white/80">
                            If an admin account exists for <strong className="text-white">{email.trim()}</strong>, we've emailed a
                            reset link. It works once and expires in <strong className="text-white">30 minutes</strong>.
                            <div className="text-xs text-white/50 mt-2">Nothing in your inbox? Check spam, or try again in 15 minutes.</div>
                        </div>
                    </div>
                    <div className="mt-5"><BackToLogin /></div>
                </div>
            ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="forgot-password-form">
                    <p className="text-sm text-white/60">Enter the email you sign in with and we'll send you a single-use link to choose a new password.</p>
                    <label className="block">
                        <div className="label-xs mb-2">Email</div>
                        <input
                            type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)}
                            className={AUTH_INPUT_CLS} placeholder="you@example.com" spellCheck={false}
                            data-testid="forgot-password-email-input"
                        />
                    </label>
                    <AuthError message={error} testId="forgot-password-error" />
                    <button type="submit" disabled={busy || !email.trim()} className={AUTH_BUTTON_CLS} data-testid="forgot-password-submit">
                        {busy ? "Sending…" : "Email me a reset link"}
                    </button>
                    <div className="pt-2"><BackToLogin /></div>
                </form>
            )}
        </AuthShell>
    );
}
